import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { FileText, CheckCircle, Wifi, WifiOff, Smartphone, Clock, Download, ArrowRight } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-emerald-600" />
            <span className="text-xl font-semibold text-slate-800">CurrículoPRO</span>
          </div>
          <Link to="/editor">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200">
              Criar Currículo
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 leading-tight">
            Crie seu currículo profissional em
            <span className="text-emerald-600"> 5 minutos</span>
          </h1>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Sem cadastro, sem complicação. Gere um PDF pronto para envio direto do seu celular ou computador.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/editor">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 w-full sm:w-auto transition-all duration-200 hover:scale-105">
                Criar currículo grátis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/editor?preview=true">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto border-slate-300 hover:bg-slate-100 transition-colors duration-200">
                Ver modelo de currículo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-12">
            Por que usar o CurrículoPRO?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <BenefitCard
              icon={<CheckCircle className="w-10 h-10 text-emerald-600" />}
              title="Modelos Modernos"
              description="Design limpo e profissional compatível com sistemas ATS"
            />
            <BenefitCard
              icon={<WifiOff className="w-10 h-10 text-emerald-600" />}
              title="Funciona Offline"
              description="Seus dados ficam salvos no seu dispositivo"
            />
            <BenefitCard
              icon={<Download className="w-10 h-10 text-emerald-600" />}
              title="PDF Pronto"
              description="Baixe seu currículo em alta qualidade instantaneamente"
            />
            <BenefitCard
              icon={<Smartphone className="w-10 h-10 text-emerald-600" />}
              title="Mobile-First"
              description="Ideal para criar currículos pelo celular"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-12">
            Como funciona
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number="1"
              title="Preencha seus dados"
              description="Informações pessoais, experiências, formação e habilidades"
            />
            <StepCard
              number="2"
              title="Visualize em tempo real"
              description="Veja como seu currículo está ficando enquanto preenche"
            />
            <StepCard
              number="3"
              title="Baixe o PDF"
              description="Currículo pronto para enviar para vagas de emprego"
            />
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-12">
            Ideal para você
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <AudienceCard title="Primeiro emprego" icon={<Clock className="w-6 h-6" />} />
            <AudienceCard title="Troca de carreira" icon={<ArrowRight className="w-6 h-6" />} />
            <AudienceCard title="Profissionais CLT" icon={<FileText className="w-6 h-6" />} />
            <AudienceCard title="Autônomos e PJ" icon={<Wifi className="w-6 h-6" />} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Comece agora mesmo
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Não perca mais tempo. Crie seu currículo profissional em poucos minutos.
          </p>
          <Link to="/editor">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 text-lg px-10 py-6 transition-all duration-200 hover:scale-105">
              Criar meu currículo grátis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-400 py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="mb-4">
            Criado por{' '}
            <a
              href="https://www.youtube.com/@godgamestutorial"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200"
            >
              God Games Tutorial
            </a>
          </p>
          <div className="flex justify-center gap-6 mb-4 text-sm">
            <Link to="/privacidade" className="hover:text-emerald-400 transition-colors duration-200">
              Política de Privacidade
            </Link>
            <Link to="/termos" className="hover:text-emerald-400 transition-colors duration-200">
              Termos de Uso
            </Link>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} CurrículoPRO. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

const BenefitCard = ({ icon, title, description }) => (
  <div className="text-center p-6 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200">
    <div className="flex justify-center mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
    <p className="text-slate-600 text-sm">{description}</p>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="text-center p-6">
    <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
      {number}
    </div>
    <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
    <p className="text-slate-600 text-sm">{description}</p>
  </div>
);

const AudienceCard = ({ title, icon }) => (
  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors duration-200">
    <div className="text-emerald-600">{icon}</div>
    <span className="font-medium text-slate-700">{title}</span>
  </div>
);

export default Home;
